import {
	BasicType,
	AdvancedType,
	DynamicType,
	IBlockData,
	getIndexByIdx,
	getPageIdx,
	getParentByIdx,
	getParentIdx,
	getValueByIdx,
	BlockManager,
	createBlockDataByType,
	isValidParentType,
	isListingSectionPayload,
	isDeniedListingPlacement,
	getAncestorListingWrapper,
	getAncestorCustomListing,
	CUSTOM_LISTING_BLOCK_TYPE,
	canAddRootChild,
	ROOT_WRAPPER_MESSAGES,
} from '@bay-email/core';
import { cloneDeep, debounce, get } from 'lodash';
import { useCallback, useContext } from 'react';

import { useEditorContext } from './useEditorContext';
import { RecordContext } from '@/components/Provider/RecordProvider';
import { useFocusIdx } from './useFocusIdx';
import { IEmailTemplate } from '@/typings';
import { useEditorProps } from './useEditorProps';
import { scrollBlockEleIntoView } from '@/utils';

type PageTextDefaults = {
	fontFamily?: string;
	fontSize?: string;
	fontWeight?: string;
	lineHeight?: string;
	textColor?: string;
};

const TEXT_CAPABLE_BLOCK_TYPES = new Set<string>([
	BasicType.TEXT,
	BasicType.BUTTON,
	BasicType.SOCIAL,
	BasicType.ACCORDION_TITLE,
	BasicType.ACCORDION_TEXT,
]);

const DYNAMIC_BLOCK_TYPES = new Set<string>([DynamicType.IMAGE]);

function applyPageTextDefaultsToBlock(
	block: IBlockData,
	defaults: PageTextDefaults,
): IBlockData {
	const nextBlock = cloneDeep(block);
	const attributes = nextBlock.attributes || {};
	const isButtonBlock =
		nextBlock.type === BasicType.BUTTON || nextBlock.type === AdvancedType.BUTTON;
	const supportsTextDefaults =
		TEXT_CAPABLE_BLOCK_TYPES.has(nextBlock.type) ||
		'font-family' in attributes ||
		'font-size' in attributes ||
		'font-weight' in attributes ||
		'line-height' in attributes;

	if (supportsTextDefaults) {
		nextBlock.attributes = {
			...attributes,
			...(defaults.fontFamily ? { 'font-family': defaults.fontFamily } : {}),
			...(defaults.fontSize ? { 'font-size': defaults.fontSize } : {}),
			...(defaults.fontWeight ? { 'font-weight': defaults.fontWeight } : {}),
			...(defaults.lineHeight ? { 'line-height': defaults.lineHeight } : {}),
			...(!isButtonBlock && defaults.textColor ? { color: defaults.textColor } : {}),
		};
	}

	if (Array.isArray(nextBlock.children) && nextBlock.children.length > 0) {
		nextBlock.children = nextBlock.children.map(child =>
			applyPageTextDefaultsToBlock(child, defaults),
		);
	}

	return nextBlock;
}

export function useBlock() {
	const {
		formState: { values },
		formHelpers: { getState, change },
	} = useEditorContext();

	const { focusIdx, setFocusIdx } = useFocusIdx();

	const { autoComplete, blockMode } = useEditorProps();

	const focusBlock = get(values, focusIdx) as IBlockData | null;

	const { redo, undo, redoable, undoable, reset } = useContext(RecordContext);

	const addBlock = useCallback(
		(params: {
			type: string;
			parentIdx: string;
			positionIndex?: number;
			payload?: any;
			canReplace?: boolean;
		}) => {
			let { type, parentIdx, positionIndex, payload } = params;
			let nextFocusIdx: string;

			// Reused block dropped whole: a saved block's payload is a Page holding
			// its root wrapper(s) (up to a plain Wrapper + a Custom Listing Wrapper).
			// The drag type is a wrapper (so dragover accepts it at the email root),
			// but the payload is the Page — fan it out, inserting ALL its wrappers as
			// siblings at the drop target in ONE state write. (Looping addBlock per
			// wrapper races: each reads/writes form state separately, so the second
			// insert clobbers the first — only one wrapper survived.)
			if (payload?.type === BasicType.PAGE && Array.isArray(payload?.children)) {
				const roots = (payload.children as IBlockData[]).filter(Boolean);
				if (roots.length) {
					const nextValues = cloneDeep(getState().values) as IEmailTemplate;
					const dropParent = get(nextValues, parentIdx) as IBlockData | null;
					if (!dropParent) {
						console.error(`Invalid ${type} block`);
						return;
					}
					let at =
						typeof positionIndex === 'number'
							? positionIndex
							: dropParent.children.length;
					let lastIdx = parentIdx;
					let inserted = 0;
					roots.forEach((root) => {
						const rootBlock = createBlockDataByType(root.type, root);
						if (!isValidParentType(BlockManager.getBlockByType(rootBlock.type), dropParent.type)) {
							return;
						}
						const rootCheck = canAddRootChild({
							blockMode,
							parentType: dropParent.type,
							type: rootBlock.type,
							rootChildren: dropParent.children,
						});
						if (!rootCheck.ok) {
							console.error(ROOT_WRAPPER_MESSAGES[rootCheck.reason]);
							return;
						}
						dropParent.children.splice(at, 0, rootBlock);
						inserted += 1;
						lastIdx = `${parentIdx}.children.[${at}]`;
						at += 1;
					});
					if (!inserted) return;
					change(parentIdx, dropParent);
					setFocusIdx(lastIdx);
					scrollBlockEleIntoView({ idx: lastIdx });
				}
				return;
			}

			const values = cloneDeep(getState().values) as IEmailTemplate;
			const parent = get(values, parentIdx) as IBlockData | null;
			if (!parent) {
				console.error(`Invalid ${type} block`);
				return;
			}

			// A Listing section carries a listing per column, which would contradict
			// a Listing Wrapper — the wrapper's listing cascades to everything
			// inside it. Refuse the drop rather than silently ignoring the columns'
			// listings (the DnD layer also declines to offer the target).
			if (
				isListingSectionPayload(payload) &&
				(parent.type === CUSTOM_LISTING_BLOCK_TYPE ||
					getAncestorListingWrapper(values.content, parentIdx))
			) {
				console.error(
					'A Listing section cannot go inside a Listing Wrapper — that wrapper already sets one listing for everything in it.',
				);
				return;
			}

			// A listing block picks its own listing, so it can't sit inside something
			// that already supplies one. Tested on the ORIGINAL dragged type: below,
			// auto-complete wraps a Single Listing in section > column, and by then
			// the check would pass and land it inside anyway.
			if (
				isDeniedListingPlacement(
					type,
					parent,
					Boolean(getAncestorCustomListing(values.content, parentIdx)),
				)
			) {
				console.error(
					`A ${type} block cannot go inside a Listing Wrapper or a Listing Column — those already set the listing for everything in them.`,
				);
				return;
			}

			// Block editor root rule (one wrapper per type, nothing else at root): see canAddRootChild.
			const rootCheck = canAddRootChild({
				blockMode,
				parentType: parent.type,
				type,
				rootChildren: parent.children,
			});
			if (!rootCheck.ok) {
				console.error(ROOT_WRAPPER_MESSAGES[rootCheck.reason]);
				return;
			}

			let child = createBlockDataByType(type, payload);
			const pageDefaults: PageTextDefaults = {
				fontFamily: values.content.data.value['font-family'],
				fontSize: values.content.data.value['font-size'],
				fontWeight: values.content.data.value['font-weight'],
				lineHeight: values.content.data.value['line-height'],
				textColor: values.content.data.value['text-color'],
			};
			const isHeroType =
				type === BasicType.HERO ||
				type === AdvancedType.HERO ||
				child.type === BasicType.HERO ||
				child.type === AdvancedType.HERO;

			if (!payload && !isHeroType) {
				child = applyPageTextDefaultsToBlock(child, pageDefaults);
			}

			if (typeof positionIndex === 'undefined') {
				positionIndex = parent.children.length;
			}

			nextFocusIdx = `${parentIdx}.children.[${positionIndex}]`;
			const block = BlockManager.getBlockByType(type);
			if (!block) {
				console.error(`Invalid ${type} block`);
				return;
			}
			const parentBlock = BlockManager.getBlockByType(parent.type)!;

			// When the drop target isn't a legal direct parent, the block HAS to be
			// wrapped through the intermediate blocks or the insert below just
			// fails. `autoComplete` is off by default in this app, so without this
			// dropping content into a wrapper either errored out or (worse) landed
			// an mj-text directly inside an mj-wrapper, which MJML mangles and the
			// canvas can no longer select.
			const shouldAutoComplete =
				autoComplete ||
				DYNAMIC_BLOCK_TYPES.has(type) ||
				!isValidParentType(BlockManager.getBlockByType(child.type), parent.type);

			if (shouldAutoComplete) {
				const autoCompletePaths = BlockManager.getAutoCompletePath(type, parent.type);
				if (autoCompletePaths) {
					autoCompletePaths.forEach(item => {
						child = createBlockDataByType(item, {
							children: [child],
						});
						nextFocusIdx += '.children.[0]';
					});
				}
			}

			// Replace
			if (params.canReplace) {
				const parentIndex = getIndexByIdx(parentIdx);
				const upParent = getParentByIdx(values, parentIdx);
				if (upParent) {
					upParent.children.splice(parentIndex, 1, child);

					return change(getParentIdx(parentIdx)!, { ...upParent });
				}
			}

			const fixedBlock = BlockManager.getBlockByType(child.type);
			if (!isValidParentType(fixedBlock, parent.type)) {
				console.error(
					`${block.type} cannot be used inside ${
						parentBlock.type
					}, only inside: ${block.validParentType.join(', ')}`,
				);
				return;
			}

			parent.children.splice(positionIndex, 0, child);
			change(parentIdx, parent); // listeners not notified
			setFocusIdx(nextFocusIdx);
			scrollBlockEleIntoView({
				idx: nextFocusIdx,
			});
		},
		[autoComplete, blockMode, change, getState, setFocusIdx],
	);

	const moveBlock = useCallback(
		(sourceIdx: string, destinationIdx: string) => {
			if (sourceIdx === destinationIdx) return null;

			let nextFocusIdx: string;

			const values = cloneDeep(getState().values) as IEmailTemplate;
			const source = getValueByIdx(values, sourceIdx)!;
			const sourceParentIdx = getParentIdx(sourceIdx);
			const destinationParentIdx = getParentIdx(destinationIdx);
			if (!sourceParentIdx || !destinationParentIdx) return;
			const sourceParent = getValueByIdx(values, sourceParentIdx)!;
			const destinationParent = getValueByIdx(values, destinationParentIdx)!;

			const sourceIndex = getIndexByIdx(sourceIdx);
			let [removed] = sourceParent.children.splice(sourceIndex, 1);
			const shouldAutoComplete = autoComplete || DYNAMIC_BLOCK_TYPES.has(source.type);

			if (shouldAutoComplete) {
				const autoCompletePaths = BlockManager.getAutoCompletePath(
					source.type,
					destinationParent.type,
				);
				if (autoCompletePaths) {
					autoCompletePaths.forEach(item => {
						removed = createBlockDataByType(item, {
							children: [removed],
						});
						nextFocusIdx += '.children.[0]';
					});
				} else {
					console.error('Something when wrong');
				}
			}

			const positionIndex = getIndexByIdx(destinationIdx);

			if (sourceParent === destinationParent) {
				destinationParent.children.splice(positionIndex, 0, removed);

				nextFocusIdx =
					destinationParentIdx +
					`.children.[${destinationParent.children.findIndex(
						(item: IBlockData) => item === removed,
					)}]`;
			} else {
				destinationParent.children.splice(positionIndex, 0, removed);
				nextFocusIdx = destinationIdx;
			}

			change(getPageIdx(), { ...values.content });

			setTimeout(() => {
				setFocusIdx(nextFocusIdx);
			}, 50);

			scrollBlockEleIntoView({
				idx: nextFocusIdx,
			});
		},
		[autoComplete, change, getState, setFocusIdx],
	);

	const copyBlock = useCallback(
		(idx: string) => {
			let nextFocusIdx: string;
			const values = cloneDeep(getState().values) as IEmailTemplate;

			const parentIdx = getParentIdx(idx);
			if (!parentIdx) return;
			const parent = get(values, getParentIdx(idx) || '') as IBlockData | null;
			if (!parent) {
				console.error('Invalid block');
				return;
			}
			const copyBlock = cloneDeep(get(values, idx));
			const rootCheck = canAddRootChild({
				blockMode,
				parentType: parent.type,
				type: copyBlock?.type,
				rootChildren: parent.children,
			});
			if (!rootCheck.ok) {
				console.error(ROOT_WRAPPER_MESSAGES[rootCheck.reason]);
				return;
			}
			const index = getIndexByIdx(idx) + 1;

			parent.children.splice(index, 0, copyBlock);
			change(parentIdx, parent);
			nextFocusIdx = `${parentIdx}.children.[${index}]`;

			setFocusIdx(nextFocusIdx);
		},
		[blockMode, change, getState, setFocusIdx],
	);

	const removeBlock = useCallback(
		(idx: string) => {
			let nextFocusIdx: string;
			const values = cloneDeep(getState().values) as IEmailTemplate;

			const block = getValueByIdx(values, idx);
			if (!block) {
				console.error('Invalid block');
				return;
			}
			const parentIdx = getParentIdx(idx);
			const parent = get(values, getParentIdx(idx) || '') as IBlockData | null;
			const blockIndex = getIndexByIdx(idx);
			if (!parentIdx || !parent) {
				if (block.type === BasicType.PAGE) {
					console.error('Page node can not remove');
					return;
				}
				console.error('Invalid block');
				return;
			}
			nextFocusIdx = parentIdx;

			parent.children.splice(blockIndex, 1);
			change(parentIdx, parent);
			setFocusIdx(nextFocusIdx);
		},
		[change, getState, setFocusIdx],
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const setValueByIdx = useCallback(
		debounce(<T extends IBlockData>(idx: string, newVal: T) => {
			change(idx, {
				...newVal,
			});
		}),
		[change],
	);

	const isExistBlock = useCallback(
		(idx: string) => {
			return Boolean(get(values, idx));
		},
		[values],
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const setFocusBlock = useCallback(
		debounce(val => {
			change(focusIdx, { ...val });
		}),
		[focusBlock, focusIdx, change],
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const setFocusBlockValue = useCallback(
		debounce(val => {
			if (!focusBlock) return;
			focusBlock.data.value = val;
			change(focusIdx, { ...focusBlock });
		}),
		[focusBlock, focusIdx],
	);

	return {
		values,
		change,
		focusBlock,
		setFocusBlock,
		setFocusBlockValue,
		setValueByIdx,
		addBlock,
		moveBlock,
		copyBlock,
		removeBlock,
		isExistBlock,
		redo,
		undo,
		reset,
		redoable,
		undoable,
	};
}
